import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import App from './App';
import { appEntryUrl, appSignInUrl, appSignUpUrl, clerkPublishableKey, isClerkEnabled } from './config/authUrls.js';
import { getCedrumClerkLocalization, getClerkAppearance } from './ui/clerk/appearance.ts';
import './i18n';
import './styles/tokens.css';
import './styles/motion.css';
import './styles/theme.css';
import './styles/base.css';
import './styles/clerk.scss';
import './styles/tokens.scss';
import './styles/globals.scss';
import { ErrorFallback } from './monitoring/ErrorFallback';
import { Sentry } from './monitoring/sentry';

function hidePreloaderSoon() {
  if (typeof window === 'undefined') {
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      queueMicrotask(() => {
        if (window.__CEDRUM_PRELOADER__?.hide) {
          window.__CEDRUM_PRELOADER__.hide();
          return;
        }

        const fallbackPreloader = document.getElementById('cedrum-preloader');
        if (!fallbackPreloader) {
          return;
        }

        fallbackPreloader.classList.add('cedrum-preloader--hide');
        window.setTimeout(() => {
          fallbackPreloader.remove();
        }, 220);
      });
    });
  });
}

function getSystemTheme() {
  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function usePreferredTheme() {
  const [theme, setTheme] = useState(getSystemTheme);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const onThemeChange = () => {
      setTheme(mediaQuery.matches ? 'dark' : 'light');
    };

    onThemeChange();
    mediaQuery.addEventListener('change', onThemeChange);
    return () => {
      mediaQuery.removeEventListener('change', onThemeChange);
    };
  }, []);

  return theme;
}

function SiteClerkProvider({ children }) {
  const { i18n } = useTranslation();
  const theme = usePreferredTheme();
  const appearance = useMemo(
    () =>
      getClerkAppearance({
        mode: theme,
        brandVariant: 'auto',
        surface: 'embedded',
        logoLinkUrl: '/',
        termsPageUrl: '/terms',
        privacyPageUrl: '/privacy',
        helpPageUrl: '/#faq',
      }),
    [theme],
  );
  const localization = useMemo(
    () => getCedrumClerkLocalization(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage],
  );

  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      signInUrl={appSignInUrl}
      signUpUrl={appSignUpUrl}
      signInForceRedirectUrl={appEntryUrl}
      signUpForceRedirectUrl={appEntryUrl}
      appearance={appearance}
      localization={localization}
    >
      {children}
    </ClerkProvider>
  );
}

const appTree = (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      {isClerkEnabled ? (
        <SiteClerkProvider>
          {appTree}
        </SiteClerkProvider>
      ) : (
        appTree
      )}
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

hidePreloaderSoon();
